<template>
  <div>
    <!-- In Impostazioni compare solo il riquadro di lancio: l'editor vero vive in un dialog a tutto schermo -->
    <v-card outlined>
      <v-card-title class="d-flex align-center">
        <v-icon left class="mr-2">mdi-form-select</v-icon>
        {{ t('admConfig.fieldsEditor.title') }}
        <v-chip v-if="hasChanges" color="warning" size="small" label class="ml-3">
          {{ t('admConfig.fieldsEditor.unsaved') }}
        </v-chip>
      </v-card-title>
      <v-card-text>
        <div class="text-body-2 text-medium-emphasis mb-4">
          {{ t('admConfig.fieldsEditor.description') }}
        </div>
        <div class="d-flex align-center flex-wrap" style="gap: 12px;">
          <v-btn color="primary" prepend-icon="mdi-pencil-ruler" @click="openEditor">
            {{ t('admConfig.fieldsEditor.openEditor') }}
          </v-btn>
          <span class="text-caption text-medium-emphasis">
            {{ t('admConfig.fieldsEditor.fieldCount', { count: totalCount, types: projectTypes.length }) }}
          </span>
        </div>
      </v-card-text>
    </v-card>

    <!-- Editor a tutto schermo -->
    <v-dialog
      v-model="editorDialog"
      fullscreen
      :scrim="false"
      transition="dialog-bottom-transition"
    >
      <v-card class="d-flex flex-column">
        <v-toolbar density="compact" color="primary" dark class="flex-shrink-0">
          <v-btn icon @click="requestClose">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ t('admConfig.fieldsEditor.title') }}</v-toolbar-title>
          <v-chip v-if="hasChanges" color="warning" size="small" label class="ml-3">
            {{ t('admConfig.fieldsEditor.unsaved') }}
          </v-chip>
          <v-spacer />
          <v-btn
            variant="text"
            prepend-icon="mdi-undo"
            :disabled="!hasChanges || saving"
            @click="resetChanges"
          >
            {{ t('admConfig.fieldsEditor.discard') }}
          </v-btn>
          <v-btn
            variant="flat"
            color="white"
            class="text-primary ml-2"
            prepend-icon="mdi-content-save"
            :loading="saving"
            :disabled="!hasChanges"
            @click="save"
          >
            {{ t('admConfig.fieldsEditor.save') }}
          </v-btn>
        </v-toolbar>

        <v-card-text class="overflow-y-auto flex-grow-1">
          <v-alert v-if="saveError" type="error" variant="tonal" density="comfortable" class="mb-3">
            <div>{{ saveError }}</div>
            <ul v-if="saveErrorDetails.length" class="mt-2 ml-4">
              <li v-for="(e, i) in saveErrorDetails" :key="i" class="text-body-2">{{ e }}</li>
            </ul>
          </v-alert>

          <v-alert v-if="saveSuccess" type="success" variant="tonal" density="compact" class="mb-3">
            {{ saveSuccess }}
          </v-alert>

          <v-alert
            v-if="moveError"
            type="warning"
            variant="tonal"
            density="compact"
            closable
            class="mb-3"
            @click:close="moveError = ''"
          >
            {{ moveError }}
          </v-alert>

          <!-- Un tab per tipo di processo -->
          <v-tabs v-model="activeType" density="compact" color="primary" class="mb-3">
            <v-tab v-for="pt in projectTypes" :key="pt.id" :value="pt.id">
              {{ pt.descr }}
              <v-chip size="x-small" label class="ml-2">{{ countForType(pt.id) }}</v-chip>
            </v-tab>
          </v-tabs>

          <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px;">
            <v-btn size="small" color="primary" variant="tonal" prepend-icon="mdi-plus" @click="openNew">
              {{ t('admConfig.fieldsEditor.newField') }}
            </v-btn>
            <v-text-field
              v-model="search"
              density="compact"
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              :placeholder="t('admConfig.fieldsEditor.searchPlaceholder')"
              style="max-width: 280px;"
            />
            <v-spacer />
            <span class="text-caption text-medium-emphasis">
              {{ t('admConfig.fieldsEditor.dragHint') }}
            </span>
          </div>

          <v-alert
            v-if="!groupsForType.length"
            type="info"
            variant="tonal"
            density="comfortable"
          >
            {{ t('admConfig.fieldsEditor.emptyType') }}
          </v-alert>

          <!-- Campi del tipo attivo, raggruppati come nel form -->
          <v-card
            v-for="group in groupsForType"
            :key="group.description"
            variant="outlined"
            class="mb-3"
          >
            <v-card-subtitle :class="`bg-${group.color}-lighten-4 py-2 text-body-2 font-weight-medium`">
              {{ group.description || t('admConfig.fieldsEditor.noGroup') }}
              <span class="text-caption text-medium-emphasis ml-2">({{ group.entries.length }})</span>
            </v-card-subtitle>
            <v-card-text class="pa-2">
              <div
                v-for="entry in group.entries"
                :key="entry.index"
                class="field-row d-flex align-center pa-2 rounded"
                :class="{ 'field-row--dragging': dragIndex === entry.index }"
                draggable="true"
                @dragstart="onDragStart(entry.index)"
                @dragend="dragIndex = null"
                @dragover.prevent
                @drop.prevent="onDrop(entry.index)"
              >
                <v-icon size="small" color="grey" class="mr-2 drag-handle">mdi-drag</v-icon>

                <div class="flex-grow-1" style="min-width: 0;">
                  <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                    <span class="text-body-2 font-weight-medium">{{ entry.field.label || entry.field.name }}</span>
                    <code class="text-caption text-medium-emphasis">{{ entry.field.name }}</code>
                  </div>
                  <div class="d-flex align-center flex-wrap mt-1" style="gap: 4px;">
                    <v-chip size="x-small" label variant="tonal">{{ entry.field.type }}</v-chip>
                    <v-chip size="x-small" label variant="tonal" color="primary">
                      {{ describeWidth(entry.field.width) }}
                    </v-chip>
                    <v-chip v-if="entry.field.control" size="x-small" label variant="tonal" color="deep-purple">
                      control
                    </v-chip>
                    <v-chip v-if="entry.field.readonly" size="x-small" label variant="tonal" color="grey">
                      readonly
                    </v-chip>
                    <v-chip v-if="entry.field.lockAfterCreation" size="x-small" label variant="tonal" color="grey">
                      lock
                    </v-chip>
                    <v-chip v-if="entry.field.conditionalOn" size="x-small" label variant="tonal" color="orange">
                      {{ describeConditional(entry.field.conditionalOn) }}
                    </v-chip>
                    <v-chip
                      v-if="entry.field.fileMatch"
                      size="x-small" label variant="tonal" color="indigo"
                      prepend-icon="mdi-folder-search"
                    >
                      {{ (entry.field.fileMatch.patterns || []).join(', ') }}
                    </v-chip>
                    <v-chip v-if="requiredStates(entry.field).length" size="x-small" label variant="tonal" color="error">
                      {{ t('admConfig.fieldsEditor.requiredIn', { states: requiredStates(entry.field).join(', ') }) }}
                    </v-chip>
                  </div>
                </div>

                <v-btn icon variant="text" size="small" @click="openEdit(entry.index)">
                  <v-icon size="small">mdi-pencil</v-icon>
                </v-btn>
                <v-menu>
                  <template v-slot:activator="{ props }">
                    <v-btn icon variant="text" size="small" v-bind="props">
                      <v-icon size="small">mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list density="compact">
                    <v-list-subheader>{{ t('admConfig.fieldsEditor.moveTo') }}</v-list-subheader>
                    <v-list-item
                      v-for="pt in otherTypes"
                      :key="`move-${pt.id}`"
                      @click="moveToType(entry.index, pt.id)"
                    >
                      <v-list-item-title>{{ pt.descr }}</v-list-item-title>
                    </v-list-item>
                    <v-divider />
                    <v-list-subheader>{{ t('admConfig.fieldsEditor.duplicateFor') }}</v-list-subheader>
                    <v-list-item
                      v-for="pt in otherTypes"
                      :key="`dup-${pt.id}`"
                      @click="duplicateForType(entry.index, pt.id)"
                    >
                      <v-list-item-title>{{ pt.descr }}</v-list-item-title>
                    </v-list-item>
                    <v-divider />
                    <v-list-item @click="askDelete(entry.index)">
                      <v-list-item-title class="text-error">
                        <v-icon size="small" class="mr-1">mdi-delete</v-icon>
                        {{ t('admConfig.fieldsEditor.delete') }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </v-card-text>
          </v-card>

          <!-- JSON risultante, utile per verifica rapida prima del salvataggio -->
          <v-expansion-panels variant="accordion" class="mt-2">
            <v-expansion-panel>
              <v-expansion-panel-title class="text-body-2">
                {{ t('admConfig.fieldsEditor.jsonPreview') }}
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <pre class="json-preview">{{ jsonPreview }}</pre>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>

        <!-- Dialog di modifica campo -->
        <v-dialog v-model="dialog" max-width="900" scrollable persistent>
          <v-card v-if="draft">
            <v-card-title class="d-flex align-center">
              <v-icon class="mr-2">{{ draftIndex === -1 ? 'mdi-plus-box' : 'mdi-pencil' }}</v-icon>
              {{ draftIndex === -1 ? t('admConfig.fieldsEditor.newField') : t('admConfig.fieldsEditor.editField') }}
            </v-card-title>
            <v-divider />
            <v-card-text style="max-height: 70vh;">
              <v-alert v-if="draftErrors.length" type="error" variant="tonal" density="compact" class="mb-3">
                <div v-for="(e, i) in draftErrors" :key="i" class="text-body-2">{{ e }}</div>
              </v-alert>

              <!-- 1. Base -->
              <div class="section-title">{{ t('admConfig.fieldsEditor.sectionBase') }}</div>
              <v-row dense>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="draft.name"
                    :label="t('admConfig.fieldsEditor.name')"
                    density="compact"
                    :hint="t('admConfig.fieldsEditor.nameHint')"
                    persistent-hint
                  />
                </v-col>
                <v-col cols="12" md="5">
                  <v-text-field v-model="draft.label" :label="t('admConfig.fieldsEditor.label')" density="compact" />
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="draft.type"
                    :items="FIELD_TYPES"
                    :label="t('admConfig.fieldsEditor.type')"
                    density="compact"
                  />
                </v-col>
              </v-row>
              <v-alert
                v-if="draftIndex !== -1 && draft.name !== originalDraftName"
                type="warning"
                variant="tonal"
                density="compact"
                class="mb-3"
              >
                {{ t('admConfig.fieldsEditor.renameWarning', { old: originalDraftName, new: draft.name }) }}
              </v-alert>

              <!-- 2. Larghezza -->
              <div class="section-title">{{ t('admConfig.fieldsEditor.sectionWidth') }}</div>
              <v-row dense>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="widthMode"
                    :items="widthModes"
                    :label="t('admConfig.fieldsEditor.widthMode')"
                    density="compact"
                  />
                </v-col>
                <template v-if="widthMode === 'token'">
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="draft.width"
                      :items="widthTokenItems"
                      :label="t('admConfig.fieldsEditor.width')"
                      density="compact"
                    />
                  </v-col>
                </template>
                <template v-else-if="widthMode === 'number'">
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="draft.width"
                      :items="columnItems"
                      :label="t('admConfig.fieldsEditor.widthColumns')"
                      density="compact"
                    />
                  </v-col>
                </template>
                <template v-else-if="widthMode === 'breakpoint'">
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="widthObj.cols"
                      :items="columnItemsWithEmpty"
                      label="cols (mobile)"
                      density="compact"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="widthObj.sm"
                      :items="columnItemsWithEmpty"
                      label="sm (tablet)"
                      density="compact"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="widthObj.md"
                      :items="columnItemsWithEmpty"
                      label="md (desktop)"
                      density="compact"
                      clearable
                    />
                  </v-col>
                </template>
              </v-row>

              <!-- 3. Gruppo -->
              <div class="section-title">{{ t('admConfig.fieldsEditor.sectionGroup') }}</div>
              <v-row dense>
                <v-col cols="12" md="7">
                  <v-combobox
                    v-model="draftGroupDescription"
                    :items="existingGroupNames"
                    :label="t('admConfig.fieldsEditor.groupName')"
                    density="compact"
                  />
                </v-col>
                <v-col cols="12" md="5">
                  <v-select
                    v-model="draftGroupColor"
                    :items="GROUP_COLORS"
                    :label="t('admConfig.fieldsEditor.groupColor')"
                    density="compact"
                  >
                    <template v-slot:item="{ props, item }">
                      <v-list-item v-bind="props">
                        <template v-slot:prepend>
                          <v-avatar :color="item.raw" size="18" />
                        </template>
                      </v-list-item>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
              <v-alert
                v-for="warn in unknownGroupWarnings"
                :key="warn.typeId"
                type="warning"
                variant="tonal"
                density="compact"
                class="mb-2"
              >
                {{ t('admConfig.fieldsEditor.unknownGroup', { group: draftGroupDescription, type: warn.typeName }) }}
              </v-alert>

              <!-- 4. Visibilità -->
              <div class="section-title">{{ t('admConfig.fieldsEditor.sectionVisibility') }}</div>
              <v-row dense>
                <v-col cols="12" md="6">
                  <!-- Il tipo si cambia dal menu della riga (Sposta in): qui è solo un'indicazione -->
                  <div class="text-caption text-medium-emphasis">
                    {{ t('admConfig.fieldsEditor.visibleIn') }}
                  </div>
                  <v-chip size="small" label variant="tonal" color="blue-grey" class="mt-1">
                    {{ draftTypeName }}
                  </v-chip>
                  <div class="text-caption text-medium-emphasis mt-1">
                    {{ t('admConfig.fieldsEditor.visibleInFixed') }}
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="conditionalMode"
                    :items="conditionalModes"
                    :label="t('admConfig.fieldsEditor.conditionalMode')"
                    density="compact"
                  />
                </v-col>
              </v-row>
              <v-row v-if="conditionalMode !== 'none'" dense>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="conditional.field"
                    :items="otherFieldNames"
                    :label="t('admConfig.fieldsEditor.conditionalField')"
                    density="compact"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-combobox
                    v-if="conditionalMode === 'values'"
                    v-model="conditional.values"
                    :items="conditionalValueSuggestions"
                    :label="t('admConfig.fieldsEditor.conditionalValues')"
                    multiple
                    chips
                    density="compact"
                  />
                  <v-combobox
                    v-else
                    v-model="conditional.value"
                    :items="conditionalValueSuggestions"
                    :label="t('admConfig.fieldsEditor.conditionalValue')"
                    density="compact"
                  />
                </v-col>
              </v-row>

              <!-- 5. Comportamento -->
              <div class="section-title">{{ t('admConfig.fieldsEditor.sectionBehaviour') }}</div>
              <v-row dense>
                <v-col cols="12" md="4">
                  <v-switch
                    v-model="draft.control"
                    :label="t('admConfig.fieldsEditor.control')"
                    color="primary" density="compact" hide-details
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-switch
                    v-model="draft.readonly"
                    :label="t('admConfig.fieldsEditor.readonly')"
                    color="primary" density="compact" hide-details
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-switch
                    v-model="draft.lockAfterCreation"
                    :label="t('admConfig.fieldsEditor.lockAfterCreation')"
                    color="primary" density="compact" hide-details
                  />
                </v-col>
              </v-row>
              <v-row dense class="mt-2">
                <v-col cols="12" md="6">
                  <v-combobox
                    v-model="editableByRoles"
                    :items="ROLES"
                    :label="t('admConfig.fieldsEditor.editableByRoles')"
                    :hint="t('admConfig.fieldsEditor.editableByRolesHint')"
                    persistent-hint
                    multiple chips clearable density="compact"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="editableInStatuses"
                    :items="statusItemsForDraft"
                    :label="t('admConfig.fieldsEditor.editableInStatuses')"
                    :hint="t('admConfig.fieldsEditor.editableInStatusesHint')"
                    persistent-hint
                    multiple chips clearable density="compact"
                  />
                </v-col>
              </v-row>

              <!-- 5b. Spunta automatica dalla cartella (solo checkbox) -->
              <template v-if="draft.type === 'checkbox'">
                <div class="section-title">{{ t('admConfig.fieldsEditor.sectionFileMatch') }}</div>
                <v-switch
                  v-model="fileMatchEnabled"
                  :label="t('admConfig.fieldsEditor.fileMatchEnabled')"
                  :hint="t('admConfig.fieldsEditor.fileMatchEnabledHint')"
                  persistent-hint
                  color="primary" density="compact"
                />
                <template v-if="fileMatchEnabled">
                  <v-row dense class="mt-2">
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="fileMatchDraft.mode"
                        :items="fileMatchModes"
                        :label="t('admConfig.fieldsEditor.fileMatchMode')"
                        density="compact" hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="8">
                      <v-combobox
                        v-model="fileMatchDraft.patterns"
                        :label="t('admConfig.fieldsEditor.fileMatchPatterns')"
                        :hint="fileMatchPatternsHint"
                        persistent-hint
                        multiple chips clearable density="compact"
                      />
                    </v-col>
                  </v-row>
                  <v-row dense class="mt-2">
                    <v-col cols="12" md="7">
                      <v-combobox
                        v-model="fileMatchDraft.folders"
                        :items="fileMatchFolderSuggestions"
                        :label="t('admConfig.fieldsEditor.fileMatchFolders')"
                        :hint="t('admConfig.fieldsEditor.fileMatchFoldersHint')"
                        persistent-hint
                        multiple chips clearable density="compact"
                      />
                    </v-col>
                    <v-col cols="12" md="5">
                      <v-combobox
                        v-model="fileMatchDraft.extensions"
                        :label="t('admConfig.fieldsEditor.fileMatchExtensions')"
                        :hint="t('admConfig.fieldsEditor.fileMatchExtensionsHint')"
                        persistent-hint
                        multiple chips clearable density="compact"
                      />
                    </v-col>
                  </v-row>
                  <v-row dense class="mt-2">
                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model.number="fileMatchDraft.minCount"
                        type="number" min="1"
                        :label="t('admConfig.fieldsEditor.fileMatchMinCount')"
                        density="compact" hide-details
                      />
                    </v-col>
                    <v-col v-if="fileMatchDraft.patterns.length > 1" cols="12" md="4">
                      <v-select
                        v-model="fileMatchDraft.match"
                        :items="fileMatchMatches"
                        :label="t('admConfig.fieldsEditor.fileMatchMatch')"
                        density="compact" hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="5">
                      <v-switch
                        v-model="fileMatchDraft.clearWhenMissing"
                        :label="t('admConfig.fieldsEditor.fileMatchClearWhenMissing')"
                        :hint="t('admConfig.fieldsEditor.fileMatchClearWhenMissingHint')"
                        persistent-hint
                        color="primary" density="compact"
                      />
                    </v-col>
                  </v-row>
                </template>
              </template>

              <!-- 6. Opzioni (solo per i tipi a scelta) -->
              <template v-if="typeHasOptions">
                <div class="section-title d-flex align-center">
                  {{ t('admConfig.fieldsEditor.sectionOptions') }}
                  <v-spacer />
                  <v-switch
                    v-model="numericOptionValues"
                    :label="t('admConfig.fieldsEditor.numericValues')"
                    color="primary" density="compact" hide-details class="mr-3"
                  />
                  <v-btn size="x-small" variant="tonal" prepend-icon="mdi-plus" @click="addOption">
                    {{ t('common.add') }}
                  </v-btn>
                </div>
                <v-alert
                  v-if="!draftOptions.length"
                  type="info" variant="tonal" density="compact" class="mb-2"
                >
                  {{ t('admConfig.fieldsEditor.noOptions') }}
                </v-alert>
                <v-row v-for="(opt, i) in draftOptions" :key="`opt-${i}`" dense align="center">
                  <v-col cols="4">
                    <v-text-field v-model="opt.value" label="value" density="compact" hide-details />
                  </v-col>
                  <v-col cols="4">
                    <v-text-field v-model="opt.text" label="text" density="compact" hide-details />
                  </v-col>
                  <!-- Colore facoltativo: il form mostra l'opzione come chip (es. situazione della DUIMP) -->
                  <v-col cols="3">
                    <v-text-field v-model="opt.color" label="color" placeholder="green" density="compact" hide-details>
                      <template v-if="opt.color" v-slot:prepend-inner>
                        <v-icon size="small" :color="opt.color">mdi-circle</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="1">
                    <v-btn icon variant="text" size="small" @click="draftOptions.splice(i, 1)">
                      <v-icon size="small" color="error">mdi-close</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </template>

              <!-- 7. Validazioni per stato -->
              <div class="section-title d-flex align-center">
                {{ t('admConfig.fieldsEditor.sectionValidation') }}
                <v-spacer />
                <v-btn size="x-small" variant="tonal" prepend-icon="mdi-plus" @click="addValidation">
                  {{ t('common.add') }}
                </v-btn>
              </div>
              <div class="text-caption text-medium-emphasis mb-2">
                {{ t('admConfig.fieldsEditor.validationHint') }}
              </div>
              <v-row v-for="(rule, i) in draftValidations" :key="`val-${i}`" dense align="center">
                <v-col cols="12" md="7">
                  <v-select
                    v-model="rule.status"
                    :items="validationStatusItems"
                    :label="t('admConfig.fieldsEditor.status')"
                    density="compact" hide-details
                  />
                </v-col>
                <v-col cols="9" md="4">
                  <v-switch
                    v-model="rule.required"
                    :label="t('admConfig.fieldsEditor.required')"
                    color="error" density="compact" hide-details
                  />
                </v-col>
                <v-col cols="3" md="1">
                  <v-btn icon variant="text" size="small" @click="draftValidations.splice(i, 1)">
                    <v-icon size="small" color="error">mdi-close</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider />
            <v-card-actions>
              <v-spacer />
              <v-btn variant="text" @click="dialog = false">{{ t('common.cancel') }}</v-btn>
              <v-btn color="primary" @click="commitDraft">{{ t('common.ok') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Conferma eliminazione, con impatto sugli altri campi -->
        <v-dialog v-model="deleteDialog" max-width="560">
          <v-card v-if="pendingDelete">
            <v-card-title class="text-error">
              <v-icon class="mr-2">mdi-alert</v-icon>
              {{ t('admConfig.fieldsEditor.deleteTitle') }}
            </v-card-title>
            <v-card-text>
              <p>{{ t('admConfig.fieldsEditor.deleteConfirm', { name: pendingDelete.field.name, type: pendingDelete.typeName }) }}</p>
              <v-alert v-if="pendingDelete.dependents.length" type="warning" variant="tonal" density="compact" class="mt-3">
                {{ t('admConfig.fieldsEditor.deleteDependents') }}
                <ul class="ml-4 mt-1">
                  <li v-for="d in pendingDelete.dependents" :key="d" class="text-body-2">{{ d }}</li>
                </ul>
              </v-alert>
              <v-alert type="info" variant="tonal" density="compact" class="mt-3">
                {{ t('admConfig.fieldsEditor.deleteDataNote') }}
              </v-alert>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn variant="text" @click="deleteDialog = false">{{ t('common.cancel') }}</v-btn>
              <v-btn color="error" @click="confirmDelete">{{ t('common.delete') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card>
    </v-dialog>

    <!-- Chiusura con modifiche pendenti: senza questa conferma la copia di lavoro andrebbe persa -->
    <v-dialog v-model="confirmCloseDialog" max-width="480">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2" color="warning">mdi-alert</v-icon>
          {{ t('admConfig.fieldsEditor.confirmCloseTitle') }}
        </v-card-title>
        <v-card-text>{{ t('admConfig.fieldsEditor.confirmCloseText') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="confirmCloseDialog = false">
            {{ t('admConfig.fieldsEditor.keepEditing') }}
          </v-btn>
          <v-btn color="error" variant="text" @click="discardAndClose">
            {{ t('admConfig.fieldsEditor.discardAndClose') }}
          </v-btn>
          <v-btn color="primary" :loading="saving" @click="saveAndClose">
            {{ t('admConfig.fieldsEditor.saveAndClose') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useProjectStore } from '../stores/projects.js'

const { t } = useI18n()
const projectStore = useProjectStore()

// Tipi supportati dal render in ProjectForm.vue; la stessa lista è validata dal Backend
const FIELD_TYPES = [
  'text', 'number', 'currency', 'select', 'multiselect', 'date', 'datetime',
  'textarea', 'checkbox', 'notelist', 'servicequantity'
]
const TYPES_WITH_OPTIONS = ['select', 'multiselect', 'servicequantity']
const GROUP_COLORS = [
  'blue', 'green', 'orange', 'red', 'pink', 'purple', 'indigo',
  'teal', 'cyan', 'yellow', 'amber', 'brown', 'blue-grey', 'grey'
]
const ROLES = ['superadmin', 'admin', 'user']
const WIDTH_TOKENS = ['xs', 'narrow', 'normal', 'wide', 'full']
const WIDTH_TOKEN_COLS = { xs: 1, narrow: 2, normal: 3, wide: 6, full: 12 }

// Copia di lavoro: una lista per tipo di processo, come i file Classes/fields/<id>.json.
// Nulla tocca i file finché non si preme Salva.
const workingByType = ref({})
const originalJson = ref('{}')
const activeType = ref(0)
const search = ref('')
const saving = ref(false)
const saveError = ref('')
const saveErrorDetails = ref([])
const saveSuccess = ref('')
const moveError = ref('')
const dragIndex = ref(null)

const projectTypes = computed(() => projectStore.projectTypes || [])

const listFor = (typeId) => workingByType.value[String(typeId)] || []
const activeList = computed(() => listFor(activeType.value))
const totalCount = computed(() =>
  Object.values(workingByType.value).reduce((n, list) => n + list.length, 0)
)

const hasChanges = computed(() => JSON.stringify(workingByType.value) !== originalJson.value)
// Un array per tab: 86 record in un blob unico sarebbero illeggibili, e così si confronta
// direttamente con il file Classes/fields/<id>.json
const jsonPreview = computed(() => JSON.stringify(activeList.value, null, 2))

const loadFromStore = () => {
  const raw = projectStore.fieldsByProjectType || {}
  const map = {}
  // Chiavi inserite in ordine crescente: rende stabile il JSON.stringify di hasChanges.
  // Un tipo senza file compare comunque, con lista vuota.
  projectTypes.value
    .map(pt => pt.id)
    .sort((a, b) => a - b)
    .forEach(id => {
      const list = raw[String(id)]
      map[String(id)] = Array.isArray(list) ? JSON.parse(JSON.stringify(list)) : []
    })
  workingByType.value = map
  originalJson.value = JSON.stringify(map)
}

const resetChanges = () => {
  loadFromStore()
  saveError.value = ''
  saveErrorDetails.value = []
  saveSuccess.value = ''
  moveError.value = ''
}

// ---------- Apertura / chiusura dell'editor a tutto schermo ----------

const editorDialog = ref(false)
const confirmCloseDialog = ref(false)

const openEditor = async () => {
  // Riallinea alla configurazione sul server, ma senza buttare via un lavoro in corso
  if (!hasChanges.value) {
    await projectStore.loadConfigurations()
    resetChanges()
  }
  if (projectTypes.value.length && !projectTypes.value.some(pt => pt.id === activeType.value)) {
    activeType.value = projectTypes.value[0].id
  }
  editorDialog.value = true
}

const requestClose = () => {
  if (hasChanges.value) {
    confirmCloseDialog.value = true
    return
  }
  editorDialog.value = false
}

const discardAndClose = () => {
  resetChanges()
  confirmCloseDialog.value = false
  editorDialog.value = false
}

const saveAndClose = async () => {
  await save()
  // Con errore di salvataggio si resta nell'editor: chiudere perderebbe le modifiche
  if (!saveError.value) {
    confirmCloseDialog.value = false
    editorDialog.value = false
  } else {
    confirmCloseDialog.value = false
  }
}

onMounted(async () => {
  if (!projectStore.configurations?.fieldsByProjectType) {
    await projectStore.loadConfigurations()
  }
  loadFromStore()
  if (projectTypes.value.length) activeType.value = projectTypes.value[0].id
})

// ---------- Lista campi del tipo attivo ----------

const countForType = (typeId) => listFor(typeId).length

const matchesSearch = (field) => {
  const q = (search.value || '').trim().toLowerCase()
  if (!q) return true
  return `${field.name} ${field.label || ''} ${field.group?.description || ''}`.toLowerCase().includes(q)
}

// Stesso fallback e stessa regola di ordinamento gruppi di ProjectForm.vue, così l'anteprima
// nell'editor rispecchia l'ordine reale del form
const GROUP_ORDER_FALLBACK = ['Informações Básicas', 'Serviços', 'Transporte', 'Financeiro', 'Documentos', 'Observações']

const groupOrderForType = (typeId) => {
  const pt = projectTypes.value.find(p => p.id === typeId)
  return (Array.isArray(pt?.groupOrder) && pt.groupOrder.length) ? pt.groupOrder : GROUP_ORDER_FALLBACK
}

const rankOfGroup = (name, typeId) => {
  const order = groupOrderForType(typeId)
  const i = order.indexOf(name)
  if (i !== -1) return i
  // Gruppo non elencato in groupOrder: il form lo mette prima di Observações, altrimenti in coda
  const obs = order.indexOf('Observações')
  return obs !== -1 ? obs - 0.5 : order.length
}

// Raggruppa come il form: gruppi nell'ordine di cfg.json (groupOrder), campi nell'ordine dell'array
const groupsForType = computed(() => {
  const typeId = activeType.value
  const groups = new Map()

  // entry.index è l'indice DENTRO la lista del tipo attivo: tutte le azioni che lo consumano
  // partono dal tab attivo, quindi non è mai ambiguo
  activeList.value.forEach((field, index) => {
    if (!matchesSearch(field)) return
    const key = field.group?.description || ''
    if (!groups.has(key)) {
      groups.set(key, { description: key, color: field.group?.color || 'grey', entries: [] })
    }
    groups.get(key).entries.push({ index, field })
  })

  return [...groups.values()].sort((a, b) => rankOfGroup(a.description, typeId) - rankOfGroup(b.description, typeId))
})

// ---------- Riordino drag & drop ----------

const onDragStart = (index) => { dragIndex.value = index }

const onDrop = (targetIndex) => {
  const from = dragIndex.value
  dragIndex.value = null
  if (from === null || from === targetIndex) return

  const arr = activeList.value.slice()
  const target = arr[targetIndex]
  const [moved] = arr.splice(from, 1)
  const newTargetIndex = arr.indexOf(target)
  // Trascinando verso il basso il campo si posiziona dopo il target, verso l'alto prima
  arr.splice(from < targetIndex ? newTargetIndex + 1 : newTargetIndex, 0, moved)
  workingByType.value[String(activeType.value)] = arr
}

// ---------- Etichette di riepilogo nella lista ----------

const describeWidth = (width) => {
  if (width === undefined || width === null) return t('admConfig.fieldsEditor.widthDefault')
  if (typeof width === 'number') return `${width}/12`
  if (typeof width === 'string') return `${width} (${WIDTH_TOKEN_COLS[width] ?? '?'}/12)`
  const parts = ['cols', 'sm', 'md'].filter(k => width[k] !== undefined).map(k => `${k}:${width[k]}`)
  return parts.join(' ') || t('admConfig.fieldsEditor.widthDefault')
}

const describeConditional = (cond) => {
  if (!cond) return ''
  if (Array.isArray(cond.values)) return `${cond.field} ∈ ${cond.values.join('/')}`
  if (cond.notValue !== undefined) return `${cond.field} ≠ ${cond.notValue}`
  return `${cond.field} = ${cond.value}`
}

const requiredStates = (field) => {
  const v = field.validationByStatus || {}
  return Object.keys(v).filter(k => v[k]?.required)
}

const typeName = (id) => projectTypes.value.find(pt => pt.id === id)?.descr || String(id)

const otherTypes = computed(() => projectTypes.value.filter(pt => pt.id !== activeType.value))

// ---------- Spostamento / duplicazione su altro tipo processo ----------

// Un campo appartiene a un solo tipo: se il tipo di destinazione ha già quel nome, il form
// ne renderizzerebbe uno solo. Si blocca invece di sovrascrivere.
const targetHasName = (typeId, name) => listFor(typeId).some(f => f.name === name)

const moveToType = (index, typeId) => {
  const field = activeList.value[index]
  if (targetHasName(typeId, field.name)) {
    moveError.value = t('admConfig.fieldsEditor.errDuplicateTarget', { type: typeName(typeId), name: field.name })
    return
  }
  moveError.value = ''
  const moved = activeList.value.splice(index, 1)[0]
  moved.visibleInProjectTypes = [typeId]
  workingByType.value[String(typeId)].push(moved)
  activeType.value = typeId
}

const duplicateForType = (index, typeId) => {
  const field = activeList.value[index]
  if (targetHasName(typeId, field.name)) {
    moveError.value = t('admConfig.fieldsEditor.errDuplicateTarget', { type: typeName(typeId), name: field.name })
    return
  }
  moveError.value = ''
  const copy = JSON.parse(JSON.stringify(field))
  copy.visibleInProjectTypes = [typeId]
  // In coda: la lista di destinazione ha un ordinamento suo, il campo si trascina poi al suo posto
  workingByType.value[String(typeId)].push(copy)
  activeType.value = typeId
}

// ---------- Eliminazione ----------

const deleteDialog = ref(false)
const pendingDelete = ref(null)

const askDelete = (index) => {
  const field = activeList.value[index]
  // Altri campi dello STESSO tipo che dipendono da questo tramite conditionalOn:
  // resterebbero sempre nascosti
  const dependents = activeList.value
    .filter((f, i) => i !== index && f.conditionalOn?.field === field.name)
    .map(f => `${f.label || f.name} (${f.name})`)
  pendingDelete.value = { index, field, dependents, typeName: typeName(activeType.value) }
  deleteDialog.value = true
}

const confirmDelete = () => {
  activeList.value.splice(pendingDelete.value.index, 1)
  deleteDialog.value = false
  pendingDelete.value = null
}

// ---------- Dialog di modifica ----------

const dialog = ref(false)
const draft = ref(null)
const draftIndex = ref(-1)
const originalDraftName = ref('')
const draftErrors = ref([])

// Sotto-stati editabili separatamente, riassemblati in commitDraft
const widthMode = ref('token')
const widthObj = ref({ cols: null, sm: null, md: null })
const conditionalMode = ref('none')
const conditional = ref({ field: '', value: '', values: [] })
const draftOptions = ref([])
const numericOptionValues = ref(false)
const draftValidations = ref([])
const editableByRoles = ref([])
const editableInStatuses = ref([])
const fileMatchEnabled = ref(false)
const fileMatchDraft = ref({
  mode: 'docNumber', patterns: [], folders: [], extensions: [],
  minCount: 1, match: 'any', clearWhenMissing: true
})

const widthModes = computed(() => [
  { title: t('admConfig.fieldsEditor.widthModeToken'), value: 'token' },
  { title: t('admConfig.fieldsEditor.widthModeNumber'), value: 'number' },
  { title: t('admConfig.fieldsEditor.widthModeBreakpoint'), value: 'breakpoint' },
  { title: t('admConfig.fieldsEditor.widthModeDefault'), value: 'default' }
])

const widthTokenItems = computed(() =>
  WIDTH_TOKENS.map(tok => ({ title: `${tok} — ${WIDTH_TOKEN_COLS[tok]}/12`, value: tok }))
)
const columnItems = Array.from({ length: 12 }, (_, i) => ({ title: `${i + 1}/12`, value: i + 1 }))
const columnItemsWithEmpty = columnItems

const conditionalModes = computed(() => [
  { title: t('admConfig.fieldsEditor.condNone'), value: 'none' },
  { title: t('admConfig.fieldsEditor.condEquals'), value: 'value' },
  { title: t('admConfig.fieldsEditor.condNotEquals'), value: 'notValue' },
  { title: t('admConfig.fieldsEditor.condIn'), value: 'values' }
])

// Il campo in modifica appartiene al tipo del tab da cui è stato aperto
const draftType = ref(0)
const draftTypeName = computed(() => typeName(draftType.value))
const draftList = computed(() => listFor(draftType.value))

const existingGroupNames = computed(() => {
  // I gruppi configurati in cfg.json (groupOrder) esistono anche prima che un campo li usi:
  // senza di loro un gruppo appena creato non comparirebbe mai nella lista
  const names = new Set(groupOrderForType(draftType.value))
  draftList.value.forEach(f => { if (f.group?.description) names.add(f.group.description) })
  return [...names].sort((a, b) => rankOfGroup(a, draftType.value) - rankOfGroup(b, draftType.value))
})

const otherFieldNames = computed(() => {
  const names = new Set()
  draftList.value.forEach((f, i) => { if (i !== draftIndex.value && f.name) names.add(f.name) })
  return [...names].sort()
})

// Valori proposti per conditionalOn: le options del campo scelto come condizione
const conditionalValueSuggestions = computed(() => {
  const targetName = conditional.value.field
  if (!targetName) return []
  const target = draftList.value.find(f => f.name === targetName && Array.isArray(f.options))
  return (target?.options || []).map(o => String(o.value))
})

const typeHasOptions = computed(() => TYPES_WITH_OPTIONS.includes(draft.value?.type))

// ---- fileMatch: il checkbox si spunta da solo se il documento c'è nella cartella ----

const fileMatchModes = computed(() => [
  { title: t('admConfig.fieldsEditor.fileMatchModeDocNumber'), value: 'docNumber' },
  { title: t('admConfig.fieldsEditor.fileMatchModeGlob'), value: 'glob' },
  { title: t('admConfig.fieldsEditor.fileMatchModeRegex'), value: 'regex' }
])

const fileMatchMatches = computed(() => [
  { title: t('admConfig.fieldsEditor.fileMatchMatchAny'), value: 'any' },
  { title: t('admConfig.fieldsEditor.fileMatchMatchAll'), value: 'all' }
])

const fileMatchPatternsHint = computed(() => {
  const mode = fileMatchDraft.value.mode
  if (mode === 'docNumber') return t('admConfig.fieldsEditor.fileMatchPatternsHintDoc')
  if (mode === 'regex') return t('admConfig.fieldsEditor.fileMatchPatternsHintRegex')
  return t('admConfig.fieldsEditor.fileMatchPatternsHintGlob')
})

// Suggerimenti: le stesse cartelle che le azioni IA già dichiarano in cfg.json, più "." per la
// radice. Restano suggerimenti, la combobox accetta qualunque nome.
const fileMatchFolderSuggestions = computed(() => {
  const names = new Set(['.'])
  for (const action of projectStore.aiActions || []) {
    for (const folder of action?.folders || []) if (folder) names.add(folder)
    for (const step of action?.steps || []) {
      for (const folder of step?.folders || []) if (folder) names.add(folder)
    }
  }
  return [...names]
})

// Stati del solo tipo di processo del campo: prima l'unione fra tipi faceva sì che lo stesso
// id significasse due stati diversi nello stesso menu
const statusItemsForDraft = computed(() => {
  const pt = projectTypes.value.find(p => p.id === draftType.value)
  return (pt?.statusList || [])
    .map(s => ({ title: `${s.id} — ${s.descr}`, value: s.id }))
    .sort((a, b) => a.value - b.value)
})

const validationStatusItems = computed(() => [
  { title: t('admConfig.fieldsEditor.alwaysRequired'), value: '*' },
  ...statusItemsForDraft.value.map(s => ({ title: s.title, value: String(s.value) }))
])

// group è annidato: proxy per non rompere la reattività con v-model su path profondo
const draftGroupDescription = computed({
  get: () => draft.value?.group?.description || '',
  set: (v) => {
    if (!draft.value.group) draft.value.group = { description: '', color: 'grey' }
    draft.value.group.description = v || ''
  }
})
const draftGroupColor = computed({
  get: () => draft.value?.group?.color || 'grey',
  set: (v) => {
    if (!draft.value.group) draft.value.group = { description: '', color: 'grey' }
    draft.value.group.color = v || 'grey'
  }
})

// Un gruppo non presente in groupOrder (cfg.json) viene comunque mostrato, ma in una posizione
// decisa dal fallback: meglio avvisare invece di lasciar scoprire lo spostamento a salvataggio fatto
const unknownGroupWarnings = computed(() => {
  const name = draftGroupDescription.value
  if (!name || !draft.value) return []
  if (groupOrderForType(draftType.value).includes(name)) return []
  return [{ typeId: draftType.value, typeName: draftTypeName.value }]
})

const blankField = () => ({
  name: '',
  label: '',
  type: 'text',
  width: 'normal',
  control: false,
  visibleInProjectTypes: [activeType.value],
  group: { description: '', color: 'grey' },
  validationByStatus: {}
})

const loadDraft = (field) => {
  draft.value = JSON.parse(JSON.stringify(field))
  draftErrors.value = []

  // width → modalità + valori. widthObj va sempre riassegnato, altrimenti passando a
  // "per breakpoint" si riprenderebbero i valori del campo aperto in precedenza.
  const w = draft.value.width
  widthObj.value = { cols: null, sm: null, md: null }
  if (w === undefined || w === null) {
    widthMode.value = 'default'
  } else if (typeof w === 'number') {
    widthMode.value = 'number'
  } else if (typeof w === 'string') {
    widthMode.value = 'token'
  } else {
    widthMode.value = 'breakpoint'
    widthObj.value = { cols: w.cols ?? null, sm: w.sm ?? null, md: w.md ?? null }
  }

  // conditionalOn → modalità + valore
  const c = draft.value.conditionalOn
  if (!c) {
    conditionalMode.value = 'none'
    conditional.value = { field: '', value: '', values: [] }
  } else if (Array.isArray(c.values)) {
    conditionalMode.value = 'values'
    conditional.value = { field: c.field || '', value: '', values: [...c.values] }
  } else if (c.notValue !== undefined) {
    conditionalMode.value = 'notValue'
    conditional.value = { field: c.field || '', value: String(c.notValue), values: [] }
  } else {
    conditionalMode.value = 'value'
    conditional.value = { field: c.field || '', value: String(c.value ?? ''), values: [] }
  }

  // options: il tipo dei value va preservato (projectType usa numeri, gli altri stringhe)
  const opts = draft.value.options || []
  numericOptionValues.value = opts.length > 0 && opts.every(o => typeof o.value === 'number')
  draftOptions.value = opts.map(o => ({ value: String(o.value), text: o.text, color: o.color || '' }))

  // validationByStatus → righe
  const v = draft.value.validationByStatus || {}
  draftValidations.value = Object.entries(v).map(([status, rule]) => ({
    status,
    required: !!rule?.required
  }))

  editableByRoles.value = Array.isArray(draft.value.editableByRoles) ? [...draft.value.editableByRoles] : []
  editableInStatuses.value = Array.isArray(draft.value.editableInStatuses) ? [...draft.value.editableInStatuses] : []

  // fileMatch → sotto-stato del dialog
  const fm = draft.value.fileMatch
  fileMatchEnabled.value = !!fm
  fileMatchDraft.value = {
    mode: fm?.mode || 'docNumber',
    patterns: Array.isArray(fm?.patterns) ? [...fm.patterns] : [],
    folders: Array.isArray(fm?.folders) ? [...fm.folders] : [],
    extensions: Array.isArray(fm?.extensions) ? [...fm.extensions] : [],
    minCount: fm?.minCount ?? 1,
    match: fm?.match || 'any',
    clearWhenMissing: fm?.clearWhenMissing !== false
  }
}

const openEdit = (index) => {
  draftIndex.value = index
  draftType.value = activeType.value
  originalDraftName.value = activeList.value[index].name
  loadDraft(activeList.value[index])
  dialog.value = true
}

const openNew = () => {
  draftIndex.value = -1
  draftType.value = activeType.value
  originalDraftName.value = ''
  loadDraft(blankField())
  dialog.value = true
}

// Passando a token/numero si riparte da un valore sensato invece che da undefined
watch(widthMode, (mode) => {
  if (!draft.value) return
  if (mode === 'token' && typeof draft.value.width !== 'string') draft.value.width = 'normal'
  if (mode === 'number' && typeof draft.value.width !== 'number') draft.value.width = 3
})

const addOption = () => draftOptions.value.push({ value: '', text: '', color: '' })
const addValidation = () => draftValidations.value.push({ status: '*', required: true })

const commitDraft = () => {
  const f = draft.value
  const errors = []

  if (!f.name || !/^[A-Za-z_][A-Za-z0-9_]*$/.test(f.name)) {
    errors.push(t('admConfig.fieldsEditor.errName'))
  }
  // Stesso name due volte nello stesso tipo: il form ne renderizzerebbe uno solo.
  // Fra tipi diversi lo stesso nome è invece legittimo e atteso.
  const clash = draftList.value.some((other, i) => i !== draftIndex.value && other.name === f.name)
  if (clash) errors.push(t('admConfig.fieldsEditor.errDuplicate', { name: f.name, type: draftTypeName.value }))

  if (conditionalMode.value !== 'none' && !conditional.value.field) {
    errors.push(t('admConfig.fieldsEditor.errConditionalField'))
  }

  // fileMatch: gli errori vanno visti qui, non come un checkbox che in produzione resta
  // misteriosamente vuoto. Il Backend riapplica le stesse regole al salvataggio.
  const fmPatterns = fileMatchDraft.value.patterns.map(p => String(p).trim()).filter(Boolean)
  const fmActive = f.type === 'checkbox' && fileMatchEnabled.value
  if (fmActive) {
    if (!fmPatterns.length) {
      errors.push(t('admConfig.fieldsEditor.errFileMatchPatterns'))
    }
    if (fileMatchDraft.value.mode === 'docNumber') {
      for (const p of fmPatterns) {
        if (!/^\d{1,6}$/.test(p)) errors.push(t('admConfig.fieldsEditor.errFileMatchDocNumber', { pattern: p }))
      }
    } else if (fileMatchDraft.value.mode === 'regex') {
      for (const p of fmPatterns) {
        try { new RegExp(p) } catch (e) {
          errors.push(t('admConfig.fieldsEditor.errFileMatchRegex', { pattern: p, message: e.message }))
        }
      }
    }
  }

  draftErrors.value = errors
  if (errors.length) return

  // width
  if (widthMode.value === 'default') {
    delete f.width
  } else if (widthMode.value === 'breakpoint') {
    const w = {}
    for (const key of ['cols', 'sm', 'md']) {
      if (widthObj.value[key]) w[key] = widthObj.value[key]
    }
    if (Object.keys(w).length) f.width = w
    else delete f.width
  }

  // conditionalOn
  if (conditionalMode.value === 'none') {
    delete f.conditionalOn
  } else if (conditionalMode.value === 'values') {
    f.conditionalOn = { field: conditional.value.field, values: [...conditional.value.values] }
  } else if (conditionalMode.value === 'notValue') {
    f.conditionalOn = { field: conditional.value.field, notValue: conditional.value.value }
  } else {
    f.conditionalOn = { field: conditional.value.field, value: conditional.value.value }
  }

  // options
  if (TYPES_WITH_OPTIONS.includes(f.type)) {
    const opts = draftOptions.value
      .filter(o => String(o.value).length > 0)
      .map(o => ({
        value: numericOptionValues.value && !isNaN(Number(o.value)) ? Number(o.value) : o.value,
        text: o.text,
        ...(String(o.color || '').trim() ? { color: String(o.color).trim() } : {})
      }))
    if (opts.length) f.options = opts
    else delete f.options
  } else {
    delete f.options
  }

  // validationByStatus: chiave stato → { required }
  const validation = {}
  draftValidations.value.forEach(rule => {
    if (!rule.status) return
    validation[rule.status] = { required: !!rule.required }
  })
  f.validationByStatus = validation

  // liste opzionali: assenti se vuote, per non sporcare il JSON
  if (editableByRoles.value.length) f.editableByRoles = [...editableByRoles.value]
  else delete f.editableByRoles

  if (editableInStatuses.value.length) f.editableInStatuses = [...editableInStatuses.value]
  else delete f.editableInStatuses

  // fileMatch: si scrive solo il non-default, per non gonfiare i file dei campi. Il `delete` copre
  // anche il caso in cui il campo smetta di essere un checkbox.
  if (fmActive && fmPatterns.length) {
    const fm = { mode: fileMatchDraft.value.mode, patterns: fmPatterns }

    const folders = fileMatchDraft.value.folders.map(s => String(s).trim()).filter(Boolean)
    if (folders.length) fm.folders = folders

    const extensions = fileMatchDraft.value.extensions
      .map(s => String(s).trim().replace(/^\./, '').toLowerCase())
      .filter(Boolean)
    if (extensions.length) fm.extensions = extensions

    const minCount = Number(fileMatchDraft.value.minCount)
    if (minCount > 1) fm.minCount = minCount
    if (fmPatterns.length > 1 && fileMatchDraft.value.match === 'all') fm.match = 'all'
    if (fileMatchDraft.value.clearWhenMissing === false) fm.clearWhenMissing = false

    f.fileMatch = fm
  } else {
    delete f.fileMatch
  }

  // flag booleani: si tengono solo se true, tranne control che esiste sempre nel file attuale
  f.control = !!f.control
  if (!f.readonly) delete f.readonly
  if (!f.lockAfterCreation) delete f.lockAfterCreation

  // Il tipo è quello del file in cui il campo finisce: il Backend rifiuta qualunque altro valore
  f.visibleInProjectTypes = [draftType.value]

  if (draftIndex.value === -1) {
    workingByType.value[String(draftType.value)].push(f)
  } else {
    workingByType.value[String(draftType.value)][draftIndex.value] = f
  }

  dialog.value = false
}

// ---------- Salvataggio ----------

const save = async () => {
  saving.value = true
  saveError.value = ''
  saveErrorDetails.value = []
  saveSuccess.value = ''
  try {
    const result = await projectStore.saveFieldsByType(workingByType.value)
    loadFromStore()
    saveSuccess.value = t('admConfig.fieldsEditor.saved', {
      count: result?.count ?? totalCount.value,
      backup: result?.backup || '—'
    })
  } catch (e) {
    saveError.value = e?.message || t('admConfig.fieldsEditor.saveError')
    const details = e?.response?.data?.errors
    if (Array.isArray(details)) saveErrorDetails.value = details
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.field-row {
  border: 1px solid rgba(0, 0, 0, 0.08);
  margin-bottom: 4px;
  cursor: grab;
  background: rgba(255, 255, 255, 0.6);
}
.field-row:hover {
  background: rgba(0, 0, 0, 0.03);
}
.field-row--dragging {
  opacity: 0.4;
}
.drag-handle {
  cursor: grab;
}
.section-title {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(0, 0, 0, 0.6);
  margin: 20px 0 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding-bottom: 4px;
}
.json-preview {
  max-height: 360px;
  overflow: auto;
  font-size: 0.75rem;
  background: rgba(0, 0, 0, 0.03);
  padding: 8px;
  border-radius: 4px;
}
</style>
